import React, { useMemo, useState } from 'react';
import block from 'bem-cn';
import { shallowEqual, useSelector } from 'react-redux';
import { useCustomGamesModel } from 'features/creativeFunctionality/models';
import ProviderIcon from 'components/ProviderIcon';
import Switcher from 'components/Switcher';
import SVG from 'components/SVG';
import Button from 'components/Button';
import Input from 'components/Input';
import Spinner from 'components/Spinner/desktop';
import useDynamicItemsRendering from 'shared/hooks/useDynamicItemsRendering';
import { withFetchCasinoLayoutContentDesktop } from 'modules/Casino/components/withFetchCasinoLayoutContent';
import arrowSVG from './img/arrow.svg';
import searchSVG from './img/search.svg';

import './GamesCustom.scss';

const b = block('games-custom');

const GamesCustom = () => {
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const { input, output } = useCustomGamesModel();

  const { visibleItems, triggerRef } = useDynamicItemsRendering({
    allItems: output?.games,
    deps: `${JSON.stringify(output?.games)}`,
  });

  const ProvidersElements = useMemo(() => {
    return output.providers.map(p => (
      <div
        key={p.key ?? p.value}
        onClick={() => input.handleSelectProvider(p)}
        className={b('provider', { active: p.isActive })}>
        <ProviderIcon className={b('provider-icon')} name={p.value.toLowerCase()} />
        <span className={b('provider-name')}>{p.name}</span>
      </div>
    ));
  }, [output.providers]);

  const GameTypesElements = useMemo(() => {
    return output.gameTypes.map(gp => (
      <div
        key={gp.value}
        onClick={() => input.handleSelectGameType(gp)}
        className={b('game-type', { active: gp.isActive })}>
        {locale.gameTypes[gp.name] ?? gp.name}
      </div>
    ));
  }, [output.gameTypes]);

  const GamesElements = useMemo(
    () =>
      visibleItems.map(g => (
        <div key={g.key ?? g.value} className={b('game')}>
          <div className={b('wrapper')}>
            <div className={b('game-game-type')}>{locale.gameTypes[output.selectedGameType.name]}</div>
            <div className={b('game-item')}>
              <img src={g.imgUrl} alt="" className={b('game-item-icon')} />
              <span className={b('game-item-name')}>{g.name}</span>
            </div>
          </div>
          <div className={b('wrapper')}>
            {g.position > 0 && (
              <div className={b('game-switcher')}>
                <Switcher color="purple" isActive={g.isActive} onSwitch={() => input.handleSwitchGame(g)} />
              </div>
            )}
            <div className={b('game-order')}>
              <span className={b('game-order-text')}>{locale.orderOfPlay}</span>
              <div className={b('game-order-input')}>
                <Input
                  callBack={e => input.handleChangeGameOrder({ value: e.currentTarget.value, game: g })}
                  value={g.position}
                  name={`order-${g.position}`}
                />
              </div>
              {g.position > 0 && (
                <SVG
                  svgProps={{ svg: arrowSVG }}
                  onClick={() => input.handleRemoveGameOrder(g)}
                  className={b('game-order-arrow')}
                />
              )}
            </div>
          </div>
        </div>
      )),
    [visibleItems],
  );

  const GameListContent = useMemo(
    () => (
      <div ref={output.scrollRef} className={b('games')}>
        {GamesElements.length > 0 ? GamesElements : <div className={b('no-games')}>{locale.noResults}</div>}
        <div className={b('trigger')} ref={triggerRef} />
      </div>
    ),
    [GamesElements, triggerRef],
  );

  return (
    <div className={b()}>
      {output.actionProcessing && (
        <div className={b('spinner')}>
          <Spinner isLoading={output.actionProcessing} />
        </div>
      )}
      <div className={b('header')}>
        <h1 className={b('header-title')}>{locale.games}</h1>
        <span className={b('header-total')}>
          {locale.totalGames}: {output.total}
        </span>
      </div>
      <div className={b('game-types')}>{GameTypesElements}</div>
      <div className={b('providers')}>{ProvidersElements}</div>
      <div className={b('search')}>
        <div className={b('search-input')}>
          <Input
            placeholder={locale.gameNameSearch}
            value={output.searchString}
            color="transparent"
            callBack={e => input.setSearchString(e.currentTarget.value)}
          />
        </div>
        <SVG svgProps={{ svg: searchSVG }} className={b('search-button')} onClick={input.handleSendSearchResult} />
      </div>
      <div className={b('content')}>
        {GameListContent}
        <div className={b('buttons')}>
          <div className={b('button')}>
            <Button
              disabled={output.returnBackDisabled}
              color="black-transparent"
              text={locale.returnBack}
              callBack={input.handleReturnBack}
            />
          </div>
          <div className={b('button')}>
            <Button text={locale.sendToConfirm} callBack={input.handleSubmit} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withFetchCasinoLayoutContentDesktop(GamesCustom);
